import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { LessonPageService } from '../../networking/services/lesson-page.service';
import { ActivatedRoute, Router } from '@angular/router';

import { LessonPageItem } from '../../data/models/LessonPageItem';
import { LessonPageItemService } from '../../networking/services/lesson-page-item.service';
import { LessonPageComponent } from "../components/lesson-page/lesson-page.component";
import { LessonPageItemTypeEnum } from '../../data/enums/LessonPageItemType';
import { Lesson } from '../../data/models/Lesson';
import { LessonService } from '../../networking/services/lesson.service';
import { Observable } from 'rxjs';
import { DialogService } from '../../helper/services/dialog.service';
import { MatIconModule } from '@angular/material/icon';
import { LocalStorageService } from '../../helper/services/local-storage.service';
import { NavigationData } from '../../data/interfaces/NavigationData';
import { LessonPageItemProgressLocal } from '../../data/interfaces/LessonPageItemProgressLocal';
import * as uuid from 'uuid';
import { LessonPageItemType } from '../../data/models/LessonPageItemType';
import { LessonPage } from '../../data/models/LessonPage';

@Component({
    selector: 'app-lesson',
    imports: [LessonPageComponent, MatIconModule],
    templateUrl: './lesson.component.html',
    styleUrl: './lesson.component.scss'
})
export class LessonComponent implements OnInit {
  lessonPages: any = [];
  currentIndex: number = 0;
  lessonPageItems: LessonPageItem[] = [];
  lesson!: Lesson;
  selectedAnswers: string[] = [];
  highestVisitedPage: number = 0;
  newItemsLoaded: boolean = false;
  picturePath: string = ''
  @ViewChild(LessonPageComponent) lessonPageComponent!: LessonPageComponent;

  constructor(private lessonService: LessonService, private lessonPageService: LessonPageService, private activatedRoute: ActivatedRoute, private lessonPageItemService: LessonPageItemService, private router: Router, private dialogService: DialogService, private localStorageService: LocalStorageService) { }

  ngOnInit(): void {
    const lessonId = this.activatedRoute.snapshot.paramMap.get('id');
    if (lessonId) {
      this.lessonService.getLessonDetails(lessonId).subscribe((lesson) => {
        this.lesson = lesson;
        if (lesson.lesson_pages) {
          this.lessonPages = (lesson.lesson_pages as unknown as any[]).sort((a, b) => a.order - b.order);
        }

        const key = this.getVisitedPagesKey();
        const navigationData = this.localStorageService.get<NavigationData>(key);

        if (navigationData) {
          this.highestVisitedPage = navigationData.highestVisitedPage;
          this.currentIndex = navigationData.currentIndex;
        } else {
          this.highestVisitedPage = 0;
          this.currentIndex = 0;
        }
        this.goToPage(this.currentIndex)
      });
    }
  }

  loadPageItems(page: LessonPage): void {
    this.newItemsLoaded = false;
  
    if (page.items && page.items.length > 0) {
      this.lessonPageItems = page.items;
      this.markTextItemsAsCompleted(page.items);
      this.newItemsLoaded = true;
    } else {
      this.lessonPageItemService.getLessonPageItemList(page.id).subscribe((items) => {
        this.lessonPageItems = items;
        this.markTextItemsAsCompleted(items);
        this.newItemsLoaded = true;
      });
    }
  }

  goToPage(index: number) {
    if (this.currentIndex <= this.highestVisitedPage) {
      this.currentIndex = index;
      this.persistVisitedPages();
      this.loadPageItems(this.lessonPages[this.currentIndex]);
      this.loadPicture()
    }
  }

  nextPage() {
    if (this.currentIndex < this.lessonPages.length - 1) {
      this.currentIndex++;
      if (this.currentIndex > this.highestVisitedPage) {
        this.highestVisitedPage = this.currentIndex
      }
      this.persistVisitedPages();
      this.loadPageItems(this.lessonPages[this.currentIndex])
      this.loadPicture()
    }
  }

  prevPage() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.persistVisitedPages()
      this.loadPageItems(this.lessonPages[this.currentIndex])
      this.loadPicture()
    }
  }

  goToDashboard() {
    this.router.navigate(['dashboard']);
  }
  async completeLesson() {
    const message = `Super! Du hast die Lektion „${this.lesson.title}“ abgeschlossen.`;
    const result = await this.dialogService.openQuizSolutionDialog({
      title: 'Lektion geschafft!',
      logoUrl: 'assets/images/Baerchen_Party.png',
      messages: [message],
      primaryButtonText: 'Weiter'
    }, false);

    if (result) {
      this.goToDashboard()
    }
  }

  handleAnswerSelectionChange(selectedAnswers: string[]) {
    this.selectedAnswers = [...selectedAnswers];
  }

  async handlePressQuizButton(selectedAnswerIds: string[]) {
    const { ids, responses } = this.getResponseTextPerId(selectedAnswerIds);

    const result = await this.dialogService.openQuizSolutionDialog({
      title: 'Lernaufgabe geschafft!',
      logoUrl: 'assets/images/Baerchen_Party.png',
      messages: responses,
      primaryButtonText: 'Weiter',
      secondaryButtonText: 'Frage erneut beantworten',
    }, true);

    this.addItemProgress(this.lessonPageItems[0].id, this.lessonPageItems[0].type)

    if (result) {
      this.nextPage()
    } else {
      this.selectedAnswers = []
      this.lessonPageComponent.clearSelection();
    }
  }

  getResponseTextPerId(answerIds: string[]): { ids: string[]; responses: string[] } {
    const ids: string[] = [];
    const responses: string[] = [];

    this.lessonPageItems.forEach((item) => {
      item.quiz_answers.forEach((quizAnswer) => {
        if (answerIds.includes(quizAnswer.id)) {
          ids.push(quizAnswer.id);
          responses.push(quizAnswer.response_text);
        }
      });
    });

    return { ids, responses };
  }

  isVisited(i: number): boolean {
    return i <= this.highestVisitedPage;
  }

  isLineVisible(index: number): boolean {
    return index <= this.highestVisitedPage && (index - 1) <= this.highestVisitedPage;
  }

  getVisitedPagesKey(): string {
    return `visitedPages_${this.lesson.id}`;
  }

  persistVisitedPages() {
    const key = this.getVisitedPagesKey();
    const progress: NavigationData = {
      highestVisitedPage: this.highestVisitedPage,
      currentIndex: this.currentIndex
    };
    this.localStorageService.set(key, progress);
  }

  addItemProgress(itemId: string, itemType: LessonPageItemType) {
    const existing: LessonPageItemProgressLocal[] = this.localStorageService.get(`lessonItemProgress_${this.lesson.id}`) || [];

    const alreadyExists = existing.some(p => p.itemId === itemId);
    if (alreadyExists) {
      return;
    }

    const newProgress: LessonPageItemProgressLocal = {
      key: uuid.v4(),
      itemId: itemId,
      itemType: itemType
    };

    this.localStorageService.set(`lessonItemProgress_${this.lesson.id}`, [...existing, newProgress]);
  }

  handleVideoStarted(item: LessonPageItem) {
    this.addItemProgress(item.id, item.type);
  }

  areAllItemsCompleted(): boolean {
    if(this.newItemsLoaded){
    const key = `lessonItemProgress_${this.lesson.id}`;
    const progress: LessonPageItemProgressLocal[] = this.localStorageService.get(key) || [];
  
    return this.lessonPageItems.every(item =>
      progress.some(p => p.itemId === item.id)
    );} else {
      return false
    }
  }

  private markTextItemsAsCompleted(items: LessonPageItem[]): void {
    items.forEach(item => {
      if (item.type.title === LessonPageItemTypeEnum.TEXT) {
        this.addItemProgress(item.id, item.type);
      }
    });
  }

  loadPicture() {
    switch (this.currentIndex) {
      case 0:
        this.picturePath = 'assets/images/Einleitung_Baerchen.png';
        break;
      case 1:
        this.picturePath = 'assets/images/Kursmaterial_Baerchen.png';
        break;
      case 2:
        this.picturePath = 'assets/images/Ueberleitung_Lernaufgabe_Baerchen.png';
        break;
      case 3:
        this.picturePath = 'assets/images/Lernaufgabe_Baerchen.png';
        break;
      case 4:
        this.picturePath = 'assets/images/Fazit_Baerchen.png';
        break;
      case 5:
        this.picturePath = 'assets/images/Abschluss_Baerchen.png';
        break;
      default:
        this.picturePath = 'assets/images/Baerchen_winkt.png';
        break;
    }
  }
}

